import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { Indicator } from '@/shared/ui/Indicator';
import { LoadingState, UnavailableState } from '@/shared/ui/States';

import type { Connection, ConnectorType } from '@/shared/api/generated/catalog';

import {
  useRequestHarvest,
  useSchemaPreview,
  useVerifyConnection,
} from '../api/connections';
import { RegisterConnectionForm } from './RegisterConnectionForm';

/* Registering a source as four steps: choose, configure, test, harvest.
 *
 * **Why a wizard rather than the one long form it replaces.** The form did steps 1 and 2 on one
 * screen and then stopped — a source was registered and nothing told an operator whether it *worked*
 * or what was in it. Both answers already existed as endpoints (`verify` and, now, the schema
 * preview) and neither was reachable from the place a person had just finished registering. So the
 * onboarding path ended at the moment the two most useful questions became answerable.
 *
 * **Three of the four steps are exactly what the platform can do; the fourth is narrower than the
 * design it copies, and says so.** That is worth stating in the file rather than discovering:
 *
 * * **Source** — rendered from `GET /v1/catalog/connectors`, so the tiles are the families this
 *   deployment can actually use, with the unusable ones visible and explained. A fixed grid of twelve
 *   logos would be nine fictions.
 * * **Credentials** — the fields come from that same profile's `required_fields`, so a family's form
 *   is the server's answer rather than a hardcoded shape per provider.
 * * **Test** — `POST …/verify` answers *one* question: was the source reachable with this credential.
 *   The design this follows shows six checks — credential, network, TLS, per-schema permissions with
 *   a grant script, resource-manager group, server version. **Five of those are not things this
 *   platform measures**, and inventing rows for them would be a test report that reports nothing. One
 *   real check is rendered, and the step says what it covers.
 * * **Datasets** — the preview lists the schemas a harvest would cover and how many objects are in
 *   each, which is real. **Choosing a subset is not**: `POST …/harvest` takes no scope, so a harvest
 *   covers everything the credential can see. The step shows the total it is about to cover rather
 *   than checkboxes that would not be honoured — a selection the server ignores is worse than no
 *   selection, because the operator believes they narrowed it.
 */

type Step = 'source' | 'credentials' | 'test' | 'datasets';

const ORDER: readonly Step[] = ['source', 'credentials', 'test', 'datasets'];

export function NewConnectionWizard({
  onFinished,
  onCancel,
}: {
  /** Called with the source's name once the harvest is requested, so the screen can confirm it. */
  readonly onFinished: (name: string) => void;
  readonly onCancel: () => void;
}): ReactElement {
  const { t } = useTranslation();

  const [step, setStep] = useState<Step>('source');
  /* `null` until step 1 is answered, which is what gates Continue. Not defaulted to a provider: a
   * pre-selected tile is a choice the operator did not make, and on this screen the choice decides
   * which credential they are about to type. */
  const [connector, setConnector] = useState<ConnectorType | null>(null);
  const [registered, setRegistered] = useState<Connection | null>(null);

  const verify = useVerifyConnection();
  const harvest = useRequestHarvest();
  /* Only fetched once step 4 is reached. The hook is `enabled`-gated on this id, so nothing dials the
   * customer's database while the operator is still on step 2. */
  const preview = useSchemaPreview(
    step === 'datasets' && registered ? registered.id : null,
  );

  const index = ORDER.indexOf(step);

  return (
    <Card
      title={t('wizard.title')}
      hint={t('wizard.subtitle')}
      /* The subtitle is the platform's own promise and belongs on this screen above all others: the
         operator is about to hand over a credential to a production database. */
    >
      <ol className="lq-stepper">
        {ORDER.map((each, position) => {
          const state =
            position < index ? 'done' : position === index ? 'current' : 'ahead';
          return (
            <li
              key={each}
              className="lq-stepper__step"
              /* `data-state` rather than three classes: a step is exactly one of three things, and an
                 attribute makes that mutually exclusive by construction. */
              data-state={state}
              /* `aria-current="step"` is what a stepper is for. Deliberately not a tablist: this is a
                 sequence of states, not a set of panels a reader may click between. */
              aria-current={state === 'current' ? 'step' : undefined}
            >
              <span className="lq-stepper__disc" aria-hidden="true">
                {state === 'done' ? (
                  /* A tick, not the number. **The number and the tick say different things** — "step
                     2" against "finished" — and on a rail whose whole job is "where am I", the
                     distinction is the information. An SVG rather than the ✓ character for the reason
                     the search magnifier is one: a glyph has to be the same shape on every machine. */
                  <svg
                    viewBox="0 0 24 24"
                    width="12"
                    height="12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 12.5 9.5 18 20 6.5" />
                  </svg>
                ) : (
                  position + 1
                )}
              </span>
              {/* The state is on the disc, which is `aria-hidden`, so it is spelled out for a screen
                  reader here — otherwise the rail announces four bare words with no sense of
                  progress. */}
              <span className="lq-stepper__label">
                {t(`wizard.step.${each}`)}
                <span className="lq-visually-hidden"> {t(`wizard.state.${state}`)}</span>
              </span>
            </li>
          );
        })}
      </ol>

      {/* ---------------------------------------------------------------- 1 & 2 */}
      {step === 'source' || step === 'credentials' ? (
        <>
          <RegisterConnectionForm
            step={step}
            connector={connector}
            onConnectorChange={setConnector}
            onRegistered={(connection) => {
              setRegistered(connection);
              /* Straight to the test, and the verify is fired here rather than by a button on the
                 next step: an operator who has just supplied a credential is asking "did that
                 work", and making them press Test to find out is a step that exists only because
                 the code was easier that way. */
              verify.mutate(connection.id);
              setStep('test');
            }}
          />
          {step === 'source' ? (
            <div className="lq-wizard__actions">
              <Button
                tone="primary"
                disabled={connector === null}
                onClick={() => {
                  setStep('credentials');
                }}
              >
                {t('wizard.continue')}
              </Button>
              <Button onClick={onCancel}>{t('wizard.cancel')}</Button>
            </div>
          ) : (
            <div className="lq-wizard__actions">
              {/* Back re-opens step 1 and deliberately does **not** clear the credential fields —
                  they live in the form, and the form clears them on a provider *change*, which is
                  the rule that matters. Going back to look at the tiles and returning is not a
                  provider change. */}
              <Button
                onClick={() => {
                  setStep('source');
                }}
              >
                {t('wizard.back')}
              </Button>
            </div>
          )}
        </>
      ) : null}

      {/* ---------------------------------------------------------------- 3 */}
      {step === 'test' && registered !== null ? (
        <section className="lq-wizard__panel">
          <h3 className="lq-wizard__heading">{t('wizard.test.title')}</h3>

          {verify.isPending ? (
            <LoadingState rows={1} label={t('wizard.test.running')} />
          ) : verify.isError ? (
            /* A failed *request* is not a failed source. `verify` returns a refusal as a 200 with a
               reason, so an error here means we could not ask — a different sentence, and one that
               must not mark the source as broken. */
            <Alert tone="warning" title={t('wizard.test.unasked')}>
              {verify.error.message}
            </Alert>
          ) : verify.data ? (
            <>
              <div className="lq-wizard__check">
                <Indicator
                  tone={verify.data.reachable ? 'ok' : 'danger'}
                  label={
                    verify.data.reachable
                      ? t('wizard.test.reachable')
                      : t('wizard.test.refused')
                  }
                />
                {verify.data.detail === null ? null : (
                  /* The server's own sentence. It is host-written by construction — `verify` drops
                     the driver's message because several drivers put the credential in it — so
                     rendering it verbatim is safe and is the useful half. */
                  <p className="lq-wizard__detail">{verify.data.detail}</p>
                )}
              </div>
              {/* What the check does *not* cover, stated rather than left to be assumed. An
                  operator who reads "reachable" as "permissions are correct" will discover
                  otherwise when a harvest returns a short table list, and this is the cheapest
                  place to prevent that. */}
              <p className="lq-wizard__note">{t('wizard.test.scope')}</p>
            </>
          ) : null}

          <div className="lq-wizard__actions">
            <Button
              tone="primary"
              /* Advancing past a refused source is allowed on purpose: a credential that is not
                 yet granted is the normal state during onboarding, and the preview on the next
                 step will say plainly that it could not read the source. Blocking here would make
                 an operator start the wizard again once the DBA had granted them. */
              onClick={() => {
                setStep('datasets');
              }}
            >
              {t('wizard.test.continue')}
            </Button>
            <Button
              onClick={() => {
                verify.mutate(registered.id);
              }}
              busy={verify.isPending}
            >
              {t('wizard.test.retry')}
            </Button>
          </div>
        </section>
      ) : null}

      {/* ---------------------------------------------------------------- 4 */}
      {step === 'datasets' && registered !== null ? (
        <section className="lq-wizard__panel">
          <h3 className="lq-wizard__heading">{t('wizard.datasets.title')}</h3>

          {preview.isPending ? (
            <LoadingState rows={3} label={t('wizard.datasets.reading')} />
          ) : preview.isError ? (
            <UnavailableState
              title={t('wizard.datasets.unreadable')}
              guidance={preview.error.message}
            />
          ) : preview.data.items.length === 0 ? (
            /* Zero schemas is a real answer and usually a permissions one, so it gets a sentence
               about grants rather than an empty list. */
            <UnavailableState
              title={t('wizard.datasets.none')}
              guidance={t('wizard.datasets.noneGuidance')}
            />
          ) : (
            <>
              <ul className="lq-schemas">
                {preview.data.items.map((schema) => (
                  <li key={schema.name} className="lq-schemas__row">
                    <span className="lq-mono">{schema.name}</span>
                    <span className="lq-muted">
                      {t('wizard.datasets.objects', { count: schema.objects })}
                    </span>
                  </li>
                ))}
              </ul>
              {/* The honest version of the design's checkbox list. A harvest takes no scope, so
                  this states the total it will cover instead of offering a selection the server
                  would ignore — which is the worse outcome, because the operator would believe
                  they had narrowed it. */}
              <p className="lq-wizard__note">
                {t('wizard.datasets.scope', {
                  schemas: preview.data.meta['schemas'],
                  objects: preview.data.meta['objects'],
                })}
              </p>
            </>
          )}

          {harvest.isError ? (
            <Alert tone="error" title={t('wizard.datasets.refused')}>
              {harvest.error.message}
            </Alert>
          ) : null}

          <div className="lq-wizard__actions">
            <Button
              tone="primary"
              busy={harvest.isPending}
              onClick={() => {
                harvest.mutate(registered.id, {
                  onSuccess: () => {
                    onFinished(registered.name);
                  },
                });
              }}
            >
              {t('wizard.datasets.harvest')}
            </Button>
            {/* Finishing without harvesting is a real choice: a source registered now and harvested
                tonight is the normal shape on a large estate, and the Connections screen has its own
                Harvest control for exactly that. */}
            <Button
              onClick={() => {
                onFinished(registered.name);
              }}
            >
              {t('wizard.datasets.later')}
            </Button>
          </div>
        </section>
      ) : null}
    </Card>
  );
}
